import { proxy } from 'valtio';
import type { SourceRef } from '@/components/chat/SourcePicker';

export type ConversationUIViewMode = 'chat' | 'history';

export interface ConversationListItem {
  id: string;
  title: string;
  updatedAt: string;
  preview?: string;
  isPersisted?: boolean;
  hasMessages?: boolean;
  pageId?: string;
  /** ページ作成中フラグ — true の間はアクション一覧でクリックを無効化する */
  pendingPageCreation?: boolean;
}

export interface ConversationUiState {
  viewMode: ConversationUIViewMode;
  activeConversationId: string | null;
  conversations: ConversationListItem[];
  isListLoading: boolean;
  hasMore: boolean;
  nextCursor?: string;
  drafts: Record<string, string>;
  listInitialized: boolean;
  listError?: string;
  sidebarExpandRequested: boolean;
  /** 他画面から会話を選択してチャット画面を開くよう依頼するフラグ（navigate 直後の auto-history ガードを無効化するため） */
  chatViewRequested: boolean;
  pendingChatSources: SourceRef[];
  /**
   * ユーザーが run/prompt 開始後に手動で会話を切替えた回数を表す monotonic な token。
   * view を奪う side-effect は発火元が stamp した token と一致するときだけ実行する判定に使う。
   */
  userNavToken: number;
}

export interface ConversationUiActions {
  setViewMode: (mode: ConversationUIViewMode) => void;
  setActiveConversation: (conversationId: string | null) => void;
  setConversations: (conversations: ConversationListItem[]) => void;
  appendConversations: (conversations: ConversationListItem[], nextCursor?: string, hasMore?: boolean) => void;
  upsertConversation: (conversation: ConversationListItem) => void;
  replaceConversation: (oldId: string, nextConversation: ConversationListItem) => void;
  removeConversation: (conversationId: string) => void;
  setListLoading: (loading: boolean) => void;
  setPagination: (params: { nextCursor?: string; hasMore?: boolean }) => void;
  setDraft: (conversationId: string, draft: string) => void;
  clearDraft: (conversationId: string) => void;
  setListInitialized: (initialized: boolean) => void;
  setListError: (error?: string) => void;
  requestExpandSidebar: () => void;
  clearExpandSidebarRequest: () => void;
  requestChatView: () => void;
  clearChatViewRequest: () => void;
  requestAddChatSource: (source: SourceRef) => void;
  consumePendingChatSources: () => SourceRef[];
  /** Issue #4139: WS 切替時にアクション一覧を含む全状態をクリアして再フェッチを誘発する */
  resetForWorkspaceSwitch: () => void;
  /** ユーザーの明示的な会話切替で userNavToken を 1 進める（view 奪取ガードの基準を更新する） */
  markUserConversationSwitch: () => void;
}

const defaultState: ConversationUiState = {
  viewMode: 'history',
  activeConversationId: null,
  conversations: [],
  isListLoading: false,
  hasMore: true,
  nextCursor: undefined,
  drafts: {},
  listInitialized: false,
  listError: undefined,
  sidebarExpandRequested: false,
  chatViewRequested: false,
  pendingChatSources: [],
  userNavToken: 0,
};

export function createConversationUiStore(initialState?: Partial<ConversationUiState>) {
  const store = proxy<ConversationUiState>({
    ...defaultState,
    ...initialState,
  });

  const normalizeConversation = (conversation: ConversationListItem): ConversationListItem => {
    const isPersisted =
      typeof conversation.isPersisted === 'boolean' ? conversation.isPersisted : true;
    const hasMessages =
      typeof conversation.hasMessages === 'boolean'
        ? conversation.hasMessages
        : Boolean(conversation.preview && conversation.preview.trim().length > 0);
    return {
      ...conversation,
      isPersisted,
      hasMessages,
    };
  };

  const actions: ConversationUiActions = {
    setViewMode(mode) {
      store.viewMode = mode;
    },
    setActiveConversation(conversationId) {
      store.activeConversationId = conversationId;
    },
    setConversations(conversations) {
      store.conversations = conversations.map(normalizeConversation);
    },
    appendConversations(conversations, nextCursor, hasMore = true) {
      const normalized = conversations.map(normalizeConversation);
      store.conversations = [...store.conversations, ...normalized];
      store.nextCursor = nextCursor;
      store.hasMore = hasMore;
    },
    upsertConversation(conversation) {
      const index = store.conversations.findIndex((c) => c.id === conversation.id);
      if (index === -1) {
        const normalized = normalizeConversation(conversation);
        store.conversations = [normalized, ...store.conversations];
        return;
      }
      const merged = { ...store.conversations[index], ...conversation };
      const normalized = normalizeConversation(merged);
      const updated = [...store.conversations];
      updated[index] = normalized;
      store.conversations = updated;
    },
    replaceConversation(oldId, conversation) {
      const idx = store.conversations.findIndex((c) => c.id === oldId);
      const existing = idx !== -1 ? store.conversations[idx] : undefined;
      const merged = existing ? { ...existing, ...conversation } : conversation;
      const normalized = normalizeConversation(merged);
      if (idx !== -1) {
        const nextList = [...store.conversations];
        nextList[idx] = normalized;
        store.conversations = nextList;
      } else {
        store.conversations = [normalized, ...store.conversations];
      }
      if (store.activeConversationId === oldId) {
        store.activeConversationId = normalized.id;
      }
      if (store.drafts[oldId]) {
        store.drafts[normalized.id] = store.drafts[oldId];
        delete store.drafts[oldId];
      }
    },
    removeConversation(conversationId) {
      store.conversations = store.conversations.filter((conversation) => conversation.id !== conversationId);
      if (store.activeConversationId === conversationId) {
        store.activeConversationId = null;
      }
      if (store.drafts[conversationId]) {
        delete store.drafts[conversationId];
      }
    },
    setListLoading(loading) {
      store.isListLoading = loading;
    },
    setPagination({ nextCursor, hasMore }) {
      store.nextCursor = nextCursor;
      if (typeof hasMore === 'boolean') {
        store.hasMore = hasMore;
      }
    },
    setDraft(conversationId, draft) {
      store.drafts[conversationId] = draft;
    },
    clearDraft(conversationId) {
      delete store.drafts[conversationId];
    },
    setListInitialized(initialized) {
      store.listInitialized = initialized;
    },
    setListError(error) {
      store.listError = error;
    },
    requestExpandSidebar() {
      store.sidebarExpandRequested = true;
    },
    clearExpandSidebarRequest() {
      store.sidebarExpandRequested = false;
    },
    requestChatView() {
      store.chatViewRequested = true;
    },
    clearChatViewRequest() {
      store.chatViewRequested = false;
    },
    requestAddChatSource(source) {
      store.pendingChatSources = [...store.pendingChatSources, source];
      store.sidebarExpandRequested = true;
    },
    consumePendingChatSources() {
      const sources = store.pendingChatSources;
      store.pendingChatSources = [];
      return sources;
    },
    resetForWorkspaceSwitch() {
      // Issue #4139: WS 切替時に前 WS のアクション一覧が残らないようクリアする。
      // listInitialized を false に戻すことで useConversationListEffects の再フェッチを誘発する。
      store.activeConversationId = null;
      store.conversations = [];
      store.isListLoading = false;
      store.hasMore = true;
      store.nextCursor = undefined;
      store.drafts = {};
      store.listInitialized = false;
      store.listError = undefined;
      store.sidebarExpandRequested = false;
      store.chatViewRequested = false;
      store.pendingChatSources = [];
      store.userNavToken = 0;
    },
    markUserConversationSwitch() {
      store.userNavToken += 1;
    },
  };

  return { store, actions };
}

export type ConversationUiStoreInstance = ReturnType<typeof createConversationUiStore>;
